import React,{ useEffect, useState, useCallback, useMemo } from "react";
import PropTypes from 'prop-types';
import {
    Avatar,
    Box,
    Typography,
    MenuItem,
    Popover,
} from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import styles from "./CustomPopOver.module.scss";
import { useDispatch, useSelector } from "react-redux";
import ConfirmationDialogBox from "../CustomConfimationModal/CustomConfirmationBox.js";
import * as action from '../../../actions/index.js';
import { getLogoutChecked, getUserRoles } from '../../../selectors/auth-config-selector.js';

// Memoize UserAvatar to prevent unnecessary re-renders
const UserAvatar = React.memo(({ label = "", onClick }) => {
    return (
        <Avatar
            onClick={onClick}
            sx={{
                marginRight: "2rem",
                fontSize: "10px",
                fontWeight: "bold",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "24px",
                width: "24px",
                backgroundColor: "rgba(31, 68, 175, 1)",
                borderRadius: "50%",
                cursor: onClick ? "pointer" : "default",
                "&:hover": {
                    backgroundColor: "rgba(31, 68, 175, 1)",
                },
            }}
        >
            {label}
        </Avatar>
    );
});

UserAvatar.displayName = 'UserAvatar';

// Extract getInitials outside component to avoid re-creation on every render
const getInitials = (user) => {
    if (user.given_name || user.family_name) {
        return [user.given_name, user.family_name]
            .filter(Boolean)
            .map(n => n[0].toUpperCase())
            .join('');
    }

    if (user.name) {
        return user.name
            .split(' ')
            .map(n => n[0])
            .slice(0, 2)
            .join('')
            .toUpperCase();
    }

    if (user.email) {
        return user.email[0].toUpperCase();
    }

    return '?';
};

export default function ProfileMenu() {
    const [anchorEl, setAnchorEl] = useState(null);
    const { user, logout } = useAuth0();
    const [open, setOpen] = useState(false);
    const dispatch = useDispatch();
    const logoutConfirmation = useSelector(getLogoutChecked);
    const roles = useSelector(getUserRoles);

    // Memoize the initials calculation to avoid recalculating on every render
    const userInitials = useMemo(() => getInitials(user), [user]);

    // Memoize the roles display text
    const rolesDisplay = useMemo(() => 
        roles.length > 0
            ? roles.map(role => role.replace("GCCP - ", "")).join(", ")
            : "No Role Assigned",
        [roles]
    );

    useEffect(() => {
        if (logoutConfirmation) {
            setOpen(false);
        }
    }, [logoutConfirmation]);

    // Use useCallback to prevent function recreation on every render
    const handleClick = useCallback((event) => {
        setAnchorEl(event.currentTarget);
        setOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
        setOpen(false);
    }, []);

    const handleConfirm = useCallback(() => {
        logout({
            logoutParams: {
                returnTo: window.location.origin,
            }
        });
    }, [logout]);

    const handleUserLogout = useCallback(() => {
        dispatch(action.setLogoutChecked(true));
    }, [dispatch]);

    return (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
            <UserAvatar label={userInitials} onClick={handleClick} />
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            borderRadius: 2,
                            boxShadow: 3,
                            overflow: "visible",
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                top: -4,
                                left: "89%",
                                transform: "translateX(-50%) translateY(-50%)",
                                width: 0,
                                height: 0,
                                borderLeft: "8px solid transparent",
                                borderRight: "8px solid transparent",
                                borderBottom: "8px solid white",
                                zIndex: 1,
                            },
                            "&::after": {
                                content: '""',
                                position: "absolute",
                                top: -5,
                                left: "89%",
                                transform: "translateX(-50%) translateY(-50%)",
                                width: 0,
                                height: 0,
                                borderLeft: "9px solid transparent",
                                borderRight: "9px solid transparent",
                                borderBottom: "9px solid rgba(0,0,0,0.1)",
                                zIndex: 0,
                            },
                        },
                    }
                }}
            >
                <Box className={styles.customPopOverContainer}>
                    <Box className={styles.customProfileInfoContainer}>
                        <UserAvatar label={userInitials} />
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {user?.given_name?.toUpperCase()} {user?.family_name?.toUpperCase()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {rolesDisplay}
                            </Typography>
                        </Box>
                    </Box>
                    <MenuItem style={{ padding: "2px 0 0 0" }} disabled={true}>
                        Settings
                    </MenuItem>
                </Box>
                <MenuItem style={{ color: "rgba(31, 68, 175, 1)", cursor: "pointer" }} onClick={handleUserLogout}>
                    Logout
                </MenuItem>
            </Popover>
            <ConfirmationDialogBox
                message="Are you sure, you want to logout?"
                handleConfirm={handleConfirm}
                inputTextRequired={false}
                page="logout"
            />
        </Box>
    )
}

UserAvatar.propTypes = {
    label: PropTypes.string,
    onClick: PropTypes.func,
};